import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'lumix_cookie_consent_v2';

const defaultPreferences = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false
};

function getSavedConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.preferences || typeof parsed.preferences !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(preferences, source = 'banner') {
  const payload = {
    version: 2,
    source,
    acceptedAt: new Date().toISOString(),
    preferences: {
      necessary: true,
      preferences: !!preferences.preferences,
      analytics: !!preferences.analytics,
      marketing: !!preferences.marketing
    }
  };

  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));

  window.dispatchEvent(
    new CustomEvent('lumixCookieConsentChanged', {
      detail: payload
    })
  );

  return payload;
}

export default function CookieConsent() {
  const saved = useMemo(() => getSavedConsent(), []);

  const [visible, setVisible] = useState(!saved);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState(saved?.preferences || defaultPreferences);

  useEffect(() => {
    function abrirPreferencias() {
      const current = getSavedConsent();
      setPreferences(current?.preferences || defaultPreferences);
      setVisible(true);
      setShowSettings(true);
    }

    window.addEventListener('lumixOpenCookieSettings', abrirPreferencias);
    return () => window.removeEventListener('lumixOpenCookieSettings', abrirPreferencias);
  }, []);

  function aceitarTodos() {
    saveConsent({ necessary: true, preferences: true, analytics: true, marketing: true }, 'accept_all');
    setVisible(false);
    setShowSettings(false);
  }

  function recusarOpcionais() {
    saveConsent({ necessary: true, preferences: false, analytics: false, marketing: false }, 'reject_optional');
    setVisible(false);
    setShowSettings(false);
  }

  function salvarPreferencias() {
    saveConsent(preferences, 'custom');
    setVisible(false);
    setShowSettings(false);
  }

  function toggle(key) {
    if (key === 'necessary') return;
    setPreferences((prev) => ({
      ...prev,
      necessary: true,
      [key]: !prev[key]
    }));
  }

  if (!visible) return null;

  return (
    <>
      <style>{css}</style>

      <div className="lumix-cookie-backdrop" role="presentation">
        <section className="lumix-cookie-card" aria-label="Preferências de cookies">
          <div className="lumix-cookie-header">
            <div className="lumix-cookie-text">
              <span className="lumix-cookie-badge">Privacidade</span>
              <h3>Sua privacidade é importante</h3>

              {!showSettings ? (
                <p>
                  Usamos cookies para garantir o funcionamento da plataforma, segurança e melhorar sua experiência.
                  Você pode aceitar todos os cookies, recusar os opcionais ou personalizar suas preferências.
                </p>
              ) : (
                <p>
                  Gerencie suas preferências abaixo. Os cookies estritamente necessários permanecem ativos pois são essenciais para login, segurança e checkout.
                </p>
              )}

              <div className="lumix-cookie-links">
                <Link to="/termos-de-uso">Termos de Uso</Link>
                <Link to="/politica-de-privacidade">Política de Privacidade</Link>
                <Link to="/politica-de-cookies">Política de Cookies</Link>
              </div>
            </div>

            {/* AÇÕES QUANDO FECHADO (Ficam na lateral) */}
            {!showSettings && (
              <div className="lumix-cookie-actions">
                <button type="button" className="lumix-cookie-btn ghost" onClick={() => setShowSettings(true)}>
                  Personalizar
                </button>
                <button type="button" className="lumix-cookie-btn ghost" onClick={recusarOpcionais}>
                  Recusar opcionais
                </button>
                <button type="button" className="lumix-cookie-btn primary" onClick={aceitarTodos}>
                  Aceitar todos
                </button>
              </div>
            )}
          </div>

          {/* ÁREA DE CONFIGURAÇÕES */}
          {showSettings && (
            <>
              <div className="lumix-cookie-settings">
                <CookieToggle
                  title="Estritamente Necessários"
                  description="Essenciais para login, segurança, checkout e funcionamento básico."
                  checked={true}
                  disabled={true}
                />
                <CookieToggle
                  title="Preferências"
                  description="Lembram escolhas de interface e preferências visuais (ex: tema claro/escuro)."
                  checked={preferences.preferences}
                  onChange={() => toggle('preferences')}
                />
                <CookieToggle
                  title="Analíticos"
                  description="Ajudam a entender o uso da plataforma para melhorar performance e corrigir erros."
                  checked={preferences.analytics}
                  onChange={() => toggle('analytics')}
                />
                <CookieToggle
                  title="Marketing"
                  description="Apoiam campanhas e anúncios para manter nossa comunicação relevante para você."
                  checked={preferences.marketing}
                  onChange={() => toggle('marketing')}
                />
              </div>

              {/* AÇÕES QUANDO ABERTO (Ficam embaixo) */}
              <div className="lumix-cookie-actions footer-actions">
                <button type="button" className="lumix-cookie-btn ghost" onClick={() => setShowSettings(false)}>
                  Voltar
                </button>
                <button type="button" className="lumix-cookie-btn ghost" onClick={recusarOpcionais}>
                  Recusar opcionais
                </button>
                <button type="button" className="lumix-cookie-btn primary" onClick={salvarPreferencias}>
                  Salvar preferências
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </>
  );
}

function CookieToggle({ title, description, checked, disabled, onChange }) {
  return (
    <div className={`lumix-cookie-toggle ${disabled ? 'disabled' : ''}`}>
      <div className="toggle-info">
        <strong>{title}</strong>
        <small>{description}</small>
      </div>
      <button 
        type="button" 
        role="switch" 
        aria-checked={checked} 
        disabled={disabled} 
        onClick={onChange}
        className={`toggle-switch ${checked ? 'on' : 'off'} ${disabled ? 'locked' : ''}`}
      >
        <span className="toggle-knob" />
      </button>
    </div>
  );
}

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800;900&family=Inter:wght@400;500;600&display=swap');

  .lumix-cookie-backdrop {
    position: fixed;
    inset: auto 24px 24px 24px;
    z-index: 999999;
    display: flex;
    justify-content: center;
    pointer-events: none;
    animation: slideUpCookie 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes slideUpCookie {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .lumix-cookie-card {
    width: 100%;
    max-width: 960px;
    padding: 24px;
    border: 1px solid #E8EAED;
    border-radius: 16px;
    background: #FFFFFF;
    box-shadow: 0 16px 48px rgba(32, 33, 36, 0.12);
    pointer-events: auto;
    font-family: 'Nunito', 'Inter', system-ui, sans-serif;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .lumix-cookie-header {
    display: flex;
    gap: 24px;
    align-items: flex-start;
    justify-content: space-between;
  }

  .lumix-cookie-text {
    flex: 1;
  }

  .lumix-cookie-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 999px;
    background: #E8F0FE;
    color: #1A73E8;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 12px;
  }

  .lumix-cookie-text h3 {
    font-size: 18px;
    font-weight: 800;
    color: #202124;
    margin: 0 0 6px;
  }

  .lumix-cookie-text p {
    font-size: 13px;
    color: #5F6368;
    line-height: 1.5;
    margin: 0;
    max-width: 700px;
    font-weight: 500;
  }

  .lumix-cookie-links {
    display: flex;
    gap: 16px;
    margin-top: 12px;
    flex-wrap: wrap;
  }

  .lumix-cookie-links a {
    font-size: 12px;
    font-weight: 700;
    color: #1A73E8;
    text-decoration: none;
    transition: color 0.15s;
  }

  .lumix-cookie-links a:hover {
    text-decoration: underline;
    color: #1557B0;
  }

  /* LISTA DE TOGGLES */
  .lumix-cookie-settings {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 20px;
    border-top: 1px solid #E8EAED;
  }

  .lumix-cookie-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px;
    background: #F8F9FA;
    border: 1px solid #E8EAED;
    border-radius: 12px;
    transition: background 0.2s, border-color 0.2s;
  }

  .lumix-cookie-toggle:hover:not(.disabled) {
    background: #FFFFFF;
    border-color: #DADCE0;
  }

  .lumix-cookie-toggle.disabled {
    opacity: 0.8;
  }

  .toggle-info strong {
    display: block;
    font-size: 14px;
    font-weight: 800;
    color: #202124;
  }

  .toggle-info small {
    display: block;
    font-size: 12px;
    color: #5F6368;
    margin-top: 2px;
    line-height: 1.4;
    font-weight: 500;
  }

  /* SWITCH (ANDROID / GOOGLE STYLE) */
  .toggle-switch {
    width: 44px;
    height: 24px;
    border-radius: 999px;
    background: #DADCE0;
    position: relative;
    cursor: pointer;
    transition: background 0.2s ease;
    flex-shrink: 0;
  }

  .toggle-switch.on {
    background: #34A853; /* Verde sucesso */
  }

  .toggle-switch.on.locked {
    background: #1A73E8; /* Azul para os obrigatórios */
  }

  .toggle-switch:disabled {
    cursor: not-allowed;
  }

  .toggle-knob {
    width: 18px;
    height: 18px;
    background: #FFFFFF;
    border-radius: 50%;
    position: absolute;
    top: 3px;
    left: 3px;
    transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  }

  .toggle-switch.on .toggle-knob {
    transform: translateX(20px);
  }

  /* AÇÕES / BOTÕES */
  .lumix-cookie-actions {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .lumix-cookie-actions.footer-actions {
    flex-direction: row;
    justify-content: flex-end;
    padding-top: 16px;
  }

  .lumix-cookie-btn {
    min-height: 40px;
    padding: 0 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.18s ease;
    white-space: nowrap;
    width: 100%;
  }

  .footer-actions .lumix-cookie-btn {
    width: auto;
  }

  .lumix-cookie-btn.primary {
    background: #1A73E8;
    color: #FFFFFF;
    border: none;
    box-shadow: 0 1px 4px rgba(26,115,232,0.3);
  }
  .lumix-cookie-btn.primary:hover {
    background: #1557B0;
    box-shadow: 0 4px 12px rgba(26,115,232,0.4);
  }

  .lumix-cookie-btn.ghost {
    background: transparent;
    color: #5F6368;
    border: 1px solid #E8EAED;
  }
  .lumix-cookie-btn.ghost:hover {
    background: #F8F9FA;
    color: #202124;
  }

  /* RESPONSIVO */
  @media (max-width: 768px) {
    .lumix-cookie-backdrop { inset: auto 12px 12px 12px; }
    .lumix-cookie-card { padding: 20px; }
    .lumix-cookie-header { flex-direction: column; gap: 20px; }
    .lumix-cookie-actions { flex-direction: column; width: 100%; }
    .lumix-cookie-actions.footer-actions { flex-direction: column; }
    .footer-actions .lumix-cookie-btn { width: 100%; }
    .lumix-cookie-toggle { flex-direction: column; align-items: flex-start; gap: 12px; }
  }
`;